/**
 * Resource representations — what the API returns.
 *
 * These are deliberately *not* the database rows. Two differences matter:
 *
 *  - Dates cross the wire as strings: `YYYY-MM-DD` for calendar dates (the form
 *    the projection engine speaks) and ISO-8601 for timestamps.
 *  - Nothing here leaks another tenant's or another person's data. `cognitoSub`
 *    never appears; the public views (see views.ts) narrow further still.
 */

import { z } from 'zod';

import { eventStatus, ownerType, role, system, triggerType, triggerUnit, useType } from './enums.js';
import { isoDate, uuid } from './primitives.js';

const timestamp = z.string().datetime();

export const userDto = z.object({
  id: uuid,
  email: z.string().email(),
  name: z.string(),
  isAdmin: z.boolean(),
  /**
   * The two expiration notices, per user (§6). Non-nullable on the wire even
   * though the column is nullable: the API resolves null to true before it
   * gets here, so a client never has to know that null means subscribed.
   */
  remind60: z.boolean(),
  remind30: z.boolean(),
  createdAt: timestamp,
});
export type UserDto = z.infer<typeof userDto>;

/** A user's garage, as seen from their own membership. */
export const membershipDto = z.object({
  garageId: uuid,
  garageName: z.string(),
  role,
  isPublic: z.boolean(),
});
export type MembershipDto = z.infer<typeof membershipDto>;

export const garageDto = z.object({
  id: uuid,
  name: z.string(),
  isPublic: z.boolean(),
  /** Whether the public link also exposes part numbers and reorder URLs (§7). */
  publicShowMaintenance: z.boolean(),
  /**
   * Only ever populated for an owner. Crew can see *that* a garage is public
   * but are not handed the shareable link.
   */
  publicToken: z.string().nullable(),
  /** The caller's role in this garage — what the UI hides controls on. */
  role,
  createdAt: timestamp,
});
export type GarageDto = z.infer<typeof garageDto>;

/** A crew list entry. Owner-only: the member list is never public (§11). */
export const memberDto = z.object({
  userId: uuid,
  name: z.string(),
  email: z.string().email(),
  role,
  createdAt: timestamp,
});
export type MemberDto = z.infer<typeof memberDto>;

export const driverDto = z.object({
  id: uuid,
  garageId: uuid,
  name: z.string(),
  compNumber: z.string().nullable(),
  createdAt: timestamp,
});
export type DriverDto = z.infer<typeof driverDto>;

export const carDto = z.object({
  id: uuid,
  garageId: uuid,
  nickname: z.string(),
  year: z.number().int().nullable(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  motorCode: z.string().nullable(),
  powerAdder: z.string().nullable(),
  transmission: z.string().nullable(),
  raceClass: z.string().nullable(),
  useType,
  odometer: z.number().int().nullable(),
  /** A CloudFront URL minted by the API — never the raw S3 key (§7). */
  imageUrl: z.string().nullable(),
  createdAt: timestamp,
});
export type CarDto = z.infer<typeof carDto>;

export const triggerDto = z.object({
  id: uuid,
  type: triggerType,
  intervalValue: z.number().int(),
  unit: triggerUnit,
  lastCheckedAt: isoDate.nullable(),
});
export type TriggerDto = z.infer<typeof triggerDto>;

export const itemDto = z.object({
  id: uuid,
  garageId: uuid,
  ownerType,
  ownerId: uuid,
  name: z.string(),
  partNumber: z.string().nullable(),
  system,
  buyUrl: z.string().nullable(),
  isSafety: z.boolean(),
  expirationDate: isoDate.nullable(),
  spec: z.string().nullable(),
  lastServicedAt: isoDate.nullable(),
  triggers: z.array(triggerDto),
  createdAt: timestamp,
});
export type ItemDto = z.infer<typeof itemDto>;

export const eventDto = z.object({
  id: uuid,
  garageId: uuid,
  carId: uuid,
  driverId: uuid.nullable(),
  date: isoDate,
  track: z.string().nullable(),
  name: z.string(),
  status: eventStatus,
  estPasses: z.number().int(),
  estMiles: z.number().int(),
  actualPasses: z.number().int().nullable(),
  actualMiles: z.number().int().nullable(),
  createdAt: timestamp,
});
export type EventDto = z.infer<typeof eventDto>;

export const serviceLogDto = z.object({
  id: uuid,
  itemId: uuid,
  servicedAt: isoDate,
  byUserId: uuid,
  byUserName: z.string(),
  atPasses: z.number().int().nullable(),
  atMiles: z.number().int().nullable(),
  eventId: uuid.nullable(),
  cost: z.number().nullable(),
  notes: z.string().nullable(),
  createdAt: timestamp,
});
export type ServiceLogDto = z.infer<typeof serviceLogDto>;
